import api from '../api/axios';
import { Organisation } from '../models/organisation';
import { StaffProfile } from '../models/staffProfile';
import { decodeActiveUserToken } from '../utils/staffUtils';
import { clearSelectedOrgId } from './selectedOrg';

/** An organisation that has granted Rustle It support access, live right now. */
export interface SupportOrganisation {
  id: number;
  name: string;
  slug: string;
  logoUrl: string | null;
  grantId: number;
  /** When the grant ends — the API's raw TIMESTAMP string ('YYYY-MM-DD HH:MM:SS…'). */
  expiresAt: string;
  /** The Admin also let support see the status of posts and reports. */
  allowDiagnostics: boolean;
}

export interface MySupportOrganisations {
  /** True when the signed-in user is on the platform support list. */
  isSupport: boolean;
  organisations: SupportOrganisation[];
}

/** Support access — the support user's side (tdp-api docs/support-access-plan.md). */
export class SupportDataService {
  /**
   * Organisations the signed-in user can enter as support. Bootstrap auth (no
   * org header); a non-support user gets { isSupport: false } rather than a 403.
   */
  static async fetchMyOrganisations(): Promise<MySupportOrganisations> {
    const response = await api.get('/support/my-organisations');
    return {
      isSupport: response.data?.isSupport === true,
      organisations: response.data?.organisations || [],
    };
  }
}

// ── Support sessions ───────────────────────────────────────────────────────

export interface SupportSessionStart {
  activeUserToken: string;
  activeUser: StaffProfile;
  expiresAt: string;
}

/** Admin-equivalent role id the API gives support sessions (plan option A). */
const SUPPORT_ROLE_IDS = [1];

/**
 * The Organisation shape the rest of the app expects, for an org entered as
 * support. Roles mirror the session's; what support can actually do is
 * decided by the API's allow-list, not by these.
 */
export function supportOrgToOrganisation(org: SupportOrganisation): Organisation {
  return {
    id: org.id,
    name: org.name,
    slug: org.slug,
    logoUrl: org.logoUrl,
    brandColour: null,
    email: null,
    roleIds: SUPPORT_ROLE_IDS,
    roleNames: ['Support'],
    isExternal: false,
  };
}

/** True when this tab is in a support session (from the stored token). */
export function isSupportSession(): boolean {
  return decodeActiveUserToken(sessionStorage.getItem('activeUserToken'))?.isSupport === true;
}

/**
 * Leave a support session: drop the session token and the selected org and
 * go back to the picker. A full navigation, so no in-memory state from the
 * organisation survives into the next one.
 */
export function exitSupportSession(): void {
  sessionStorage.removeItem('activeUserToken');
  sessionStorage.removeItem('deviceLocked');
  clearSelectedOrgId();
  window.location.href = '/select-org';
}

export class SupportSessionService {
  /** Enter an organisation with a live grant. 403 NO_LIVE_GRANT when it has ended. */
  static async start(orgId: number): Promise<SupportSessionStart> {
    const response = await api.post(`/support/sessions/${orgId}`);
    return response.data;
  }
}

// ── Requesting access (support side) ──────────────────────────────────────

export type GrantState = 'pending' | 'lapsed' | 'active' | 'expired' | 'declined' | 'revoked';

export interface SupportRequestableOrg {
  id: number;
  name: string;
  /** Latest grant's state; null = support has never asked. */
  grantState: GrantState | null;
  requestedAt: string | null;
  expiresAt: string | null;
}

export class SupportRequestService {
  static async fetchOrganisations(): Promise<{ organisations: SupportRequestableOrg[]; dayOptions: number[] }> {
    const response = await api.get('/support/organisations');
    return {
      organisations: response.data?.organisations || [],
      dayOptions: response.data?.dayOptions || [1, 3, 7],
    };
  }

  static async requestAccess(orgId: number, body: { reason: string; days: number; ticketRef?: string }): Promise<{ id: number; adminsNotified: number }> {
    const response = await api.post(`/support/organisations/${orgId}/requests`, body);
    return response.data;
  }
}

// ── Deciding on access (organisation Admin side) ──────────────────────────

export interface SupportGrant {
  id: number;
  state: GrantState;
  /** True when an Admin gave access without a request. */
  offered: boolean;
  reason: string;
  ticketRef: string | null;
  requestedDays: number;
  requestedAt: string;
  requestedByName: string | null;
  decidedAt: string | null;
  decidedByName: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  revokedByName: string | null;
  /** Support may also see the status of posts and reports (never their content). */
  allowDiagnostics: boolean;
}

export class SupportAccessService {
  static async fetchGrants(): Promise<{ grants: SupportGrant[]; maxDays: number }> {
    const response = await api.get('/support-access');
    return { grants: response.data?.grants || [], maxDays: response.data?.maxDays ?? 14 };
  }

  static async fetchStatus(): Promise<SupportGrant | null> {
    const response = await api.get('/support-access/status');
    return response.data?.open ?? null;
  }

  static async approve(id: number, days: number, diagnostics: boolean): Promise<void> {
    await api.post(`/support-access/${id}/approve`, { days, diagnostics });
  }

  static async decline(id: number): Promise<void> {
    await api.post(`/support-access/${id}/decline`);
  }

  static async revoke(id: number): Promise<void> {
    await api.post(`/support-access/${id}/revoke`);
  }

  static async offer(reason: string, days: number, diagnostics: boolean): Promise<void> {
    await api.post('/support-access/offer', { reason, days, diagnostics });
  }

  /** Switch post & report status on or off while access is active. */
  static async setDiagnostics(id: number, enabled: boolean): Promise<void> {
    await api.post(`/support-access/${id}/diagnostics`, { enabled });
  }

  static async fetchActivity(id: number): Promise<SupportActivity> {
    const response = await api.get(`/support-access/${id}/activity`);
    return response.data;
  }

  static async downloadActivityCsv(id: number): Promise<Blob> {
    const response = await api.get(`/support-access/${id}/activity.csv`, { responseType: 'blob' });
    return response.data;
  }
}

export interface SupportActivityItem {
  kind: 'view' | 'change' | 'blocked';
  label: string;
  route: string;
  count: number;
  firstAt: string;
  entityType: string | null;
  entityId: number | null;
  /** For a change: field-level before → after, from the audit trail. */
  details?: { table: string; operation: string; changes: { field: string; before: unknown; after: unknown }[] }[];
}

export interface SupportActivity {
  grant: SupportGrant;
  /** `diagnostics` is set on an approved/offered event: what was allowed then. */
  events: { at: string; kind: string; by: string | null; diagnostics?: boolean }[];
  visits: { startedAt: string; endedAt: string; userName: string; items: SupportActivityItem[] }[];
  requestCount: number;
}

/** The code support sees for a child (matches the API's childPseudonym). */
export function childCode(id: number): string {
  return `C-${String(id).padStart(4, '0')}`;
}

/** The server's error message when it sent one, else the fallback. */
export function apiErrorMessage(err: any, fallback: string): string {
  return err?.response?.data?.error || fallback;
}

/**
 * "16 Oct, 12:24" from the API's raw TIMESTAMP string. Read as wall-clock text
 * rather than through Date, which would shift it by the browser's offset.
 */
export function formatSupportExpiry(raw: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(raw);
  if (!m) return raw;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${Number(m[3])} ${months[Number(m[2]) - 1]}, ${m[4]}:${m[5]}`;
}
